/**
 * Centralized registry for all TanStack Query keys.
 * This ensures consistency and prevents typos.
 *
 * Structure:
 * export const queryKeys = {
 *   featureName: {
 *     all: ['featureName'],
 *     lists: () => [...queryKeys.featureName.all, 'list'],
 *     list: (filters) => [...queryKeys.featureName.lists(), filters],
 *     details: () => [...queryKeys.featureName.all, 'detail'],
 *     detail: (id) => [...queryKeys.featureName.details(), id],
 *   },
 * };
 */

/**
 * Query key type - array of strings or primitives used by TanStack Query
 */
export type QueryKey = readonly (string | number)[];

/**
 * Factory function that takes optional parameters and returns a QueryKey
 */
export type QueryKeyFactory<T = unknown> = (params?: T) => QueryKey;

/**
 * Factory function that takes required parameters and returns a QueryKey
 */
export type QueryKeyFactoryRequired<T> = (params: T) => QueryKey;

// Auth
const authKeys = {
  session: ["auth", "session"] as const,
} as const;

// Users
const usersKeys = {
  all: ["users"] as const,
  me: ["users", "me"] as const,
  detail: (id: number | string) => [...usersKeys.all, "detail", id] as const,
  settings: () => [...usersKeys.all, "settings"] as const,
} as const;

// Permissions
const permissionsKeys = {
  all: ["permissions"] as const,
  current: ["permissions", "current"] as const,
  byUser: (userId: number | string) => ["permissions", userId] as const,
  byUserContext: (userId: number | string, context: string) =>
    ["permissions", userId, context] as const,
} as const;

// Collections (reference data with long stale time)
const collectionsKeys = {
  all: ["collections"] as const,
  civilites: () => [...collectionsKeys.all, "civilites"] as const,
  filiales: () => [...collectionsKeys.all, "filiales"] as const,
  typeContrats: () => [...collectionsKeys.all, "typeContrats"] as const,
  typeStatuts: () => [...collectionsKeys.all, "typeStatuts"] as const,
  typeRemunerations: () => [...collectionsKeys.all, "typeRemunerations"] as const,
  typeTempsDeTravail: () => [...collectionsKeys.all, "typeTempsDeTravail"] as const,
  etatContrats: () => [...collectionsKeys.all, "etatContrats"] as const,
  categorieDocuments: () => [...collectionsKeys.all, "categorieDocuments"] as const,
  typeDocuments: (categorieCode?: string) => [
    ...collectionsKeys.all,
    "typeDocuments",
    categorieCode || "all",
  ] as const,
  competences: (filters?: Record<string, unknown>) => [
    ...collectionsKeys.all,
    "competences",
    filters || {},
  ] as const,
  simpleCompetences: (filters?: Record<string, unknown>) => [
    ...collectionsKeys.all,
    "simpleCompetences",
    filters || {},
  ] as const,
  typeDemandes: () => [...collectionsKeys.all, "typeDemandes"] as const,
  etatsDemande: () => [...collectionsKeys.all, "etatsDemande"] as const,
  etatDemandeByCode: (code: string) => [...collectionsKeys.all, "etatDemande", code] as const,
  typePlanningItems: () => [...collectionsKeys.all, "typePlanningItems"] as const,
  planningTemplates: () => [...collectionsKeys.all, "planningTemplates"] as const,
} as const;

// API Resource query keys (used by useTable hook)
// Format: [resource, "list", {...params}]
const apiResources = {
  personnes: {
    lists: () => ["/personnes", "list"] as const,
    list: (params?: Record<string, unknown>) => [...apiResources.personnes.lists(), params || {}] as const,
  },
} as const;

// RH module query keys
const rhKeys = {
  all: ["rh"] as const,
  dashboard: () => [...rhKeys.all, "dashboard"] as const,
  metrics: () => [...rhKeys.dashboard(), "metrics"] as const,
  personnes: {
    all: () => [...rhKeys.all, "personnes"] as const,
    search: (query?: string) => [...rhKeys.personnes.all(), "search", query || ""] as const,
    managers: () => [...rhKeys.personnes.all(), "managers"] as const,
  },
  collaborateurs: {
    all: () => [...rhKeys.all, "collaborateurs"] as const,
    metrics: () => [...rhKeys.collaborateurs.all(), "metrics"] as const,
    details: () => [...rhKeys.collaborateurs.all(), "detail"] as const,
    detail: (id: number | string) => [...rhKeys.collaborateurs.details(), id] as const,
  },
  contrats: {
    all: () => [...rhKeys.all, "contrats"] as const,
    metrics: () => [...rhKeys.contrats.all(), "metrics"] as const,
    details: () => [...rhKeys.contrats.all(), "detail"] as const,
    detail: (id: number | string) => [...rhKeys.contrats.details(), id] as const,
    lists: () => [...rhKeys.contrats.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...rhKeys.contrats.lists(), filters] as const,
    byEtat: (etatCode: string) => [...rhKeys.contrats.all(), "byEtat", etatCode] as const,
    signature: (token: string) => [...rhKeys.contrats.all(), "signature", token] as const,
  },
  absences: {
    all: () => [...rhKeys.all, "absences"] as const,
    metrics: () => [...rhKeys.absences.all(), "metrics"] as const,
    list: (filters?: Record<string, unknown>) => [...rhKeys.absences.all(), "list", filters] as const,
    detail: (id: number | string) => [...rhKeys.absences.all(), "detail", id] as const,
    calendar: (filters?: Record<string, unknown>) => [...rhKeys.absences.all(), "calendar", filters] as const,
    validationState: (id: number | string) => [...rhKeys.absences.detail(id), "validation-state"] as const,
  },
  documents: {
    all: () => [...rhKeys.all, "documents"] as const,
    lists: () => [...rhKeys.documents.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...rhKeys.documents.lists(), filters || {}] as const,
    details: () => [...rhKeys.documents.all(), "detail"] as const,
    detail: (id: number | string) => [...rhKeys.documents.details(), id] as const,
    download: (id: number | string) => [...rhKeys.documents.all(), "download", id] as const,
    byPersonne: (personneId: number | string) => [...rhKeys.documents.all(), "byPersonne", personneId] as const,
    byContrat: (contratId: number | string) => [...rhKeys.documents.all(), "byContrat", contratId] as const,
    export: () => [...rhKeys.documents.all(), "export"] as const,
  },
  feuillesDeTemps: {
    all: () => [...rhKeys.all, "feuilles-de-temps"] as const,
    metrics: () => [...rhKeys.feuillesDeTemps.all(), "metrics"] as const,
    list: (filters?: Record<string, unknown>) => [...rhKeys.feuillesDeTemps.all(), "list", filters] as const,
    detail: (id: number | string) => [...rhKeys.feuillesDeTemps.all(), "detail", id] as const,
  },
  notesDeFrais: {
    all: () => [...rhKeys.all, "notes-de-frais"] as const,
    metrics: () => [...rhKeys.notesDeFrais.all(), "metrics"] as const,
    list: (filters?: Record<string, unknown>) => [...rhKeys.notesDeFrais.all(), "list", filters] as const,
    detail: (id: number | string) => [...rhKeys.notesDeFrais.all(), "detail", id] as const,
  },
  planification: {
    all: () => [...rhKeys.all, "planification"] as const,
    items: (filters?: Record<string, unknown>) => [...rhKeys.planification.all(), "items", filters || {}] as const,
    item: (id: number | string) => [...rhKeys.planification.all(), "item", id] as const,
  },
} as const;

// Pointage module query keys
const pointageKeys = {
  all: ["pointage"] as const,
  checkin: () => [...pointageKeys.all, "checkin"] as const,
} as const;

// Planning module query keys
const planningKeys = {
  all: ["planning"] as const,
  items: (filters?: Record<string, unknown>) => [...planningKeys.all, "items", filters || {}] as const,
  item: (id: number | string) => [...planningKeys.all, "item", id] as const,
  events: (personneId?: number | null, startDate?: string | null, endDate?: string | null) =>
    ["planning-events", personneId ?? null, startDate ?? null, endDate ?? null] as const,
} as const;

// Creation module query keys
const creationKeys = {
  all: ["creation"] as const,
  articles: {
    all: () => [...creationKeys.all, "articles"] as const,
    metrics: () => [...creationKeys.articles.all(), "metrics"] as const,
    lists: () => [...creationKeys.articles.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...creationKeys.articles.lists(), filters] as const,
    details: () => [...creationKeys.articles.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.articles.details(), id] as const,
    export: () => [...creationKeys.articles.all(), "export"] as const,
  },
  documents: {
    all: () => [...creationKeys.all, "documents"] as const,
    lists: () => [...creationKeys.documents.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...creationKeys.documents.lists(), filters || {}] as const,
    details: () => [...creationKeys.documents.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.documents.details(), id] as const,
    download: (id: number | string) => [...creationKeys.documents.all(), "download", id] as const,
  },
  ateliers: {
    all: () => [...creationKeys.all, "ateliers"] as const,
    lists: () => [...creationKeys.ateliers.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...creationKeys.ateliers.lists(), filters] as const,
    details: () => [...creationKeys.ateliers.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.ateliers.details(), id] as const,
  },
  postes: {
    all: () => [...creationKeys.all, "postes"] as const,
    metrics: () => [...creationKeys.postes.all(), "metrics"] as const,
    lists: () => [...creationKeys.postes.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...creationKeys.postes.lists(), filters] as const,
    details: () => [...creationKeys.postes.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.postes.details(), id] as const,
    export: () => [...creationKeys.postes.all(), "export"] as const,
  },
  prestations: {
    all: () => [...creationKeys.all, "prestations"] as const,
    lists: () => [...creationKeys.prestations.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...creationKeys.prestations.lists(), filters] as const,
    details: () => [...creationKeys.prestations.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.prestations.details(), id] as const,
  },
  dossierTechniques: {
    all: () => [...creationKeys.all, "dossierTechniques"] as const,
    lists: () => [...creationKeys.dossierTechniques.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...creationKeys.dossierTechniques.lists(), filters] as const,
    details: () => [...creationKeys.dossierTechniques.all(), "detail"] as const,
    detail: (id: number | string) => [...creationKeys.dossierTechniques.details(), id] as const,
  },
} as const;

// Stock module query keys
const stockKeys = {
  all: ["stock"] as const,
  dashboard: () => [...stockKeys.all, "dashboard"] as const,
  depots: {
    all: () => [...stockKeys.all, "depots"] as const,
    lists: () => [...stockKeys.depots.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...stockKeys.depots.lists(), filters || {}] as const,
    details: () => [...stockKeys.depots.all(), "detail"] as const,
    detail: (id: number | string) => [...stockKeys.depots.details(), id] as const,
  },
  emplacements: {
    all: () => [...stockKeys.all, "emplacements"] as const,
    lists: () => [...stockKeys.emplacements.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...stockKeys.emplacements.lists(), filters || {}] as const,
    details: () => [...stockKeys.emplacements.all(), "detail"] as const,
    detail: (id: number | string) => [...stockKeys.emplacements.details(), id] as const,
  },
  movements: {
    all: () => [...stockKeys.all, "movements"] as const,
    lists: () => [...stockKeys.movements.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...stockKeys.movements.lists(), filters || {}] as const,
  },
  inventaires: {
    all: () => [...stockKeys.all, "inventaires"] as const,
    lists: () => [...stockKeys.inventaires.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...stockKeys.inventaires.lists(), filters || {}] as const,
    details: () => [...stockKeys.inventaires.all(), "detail"] as const,
    detail: (id: number | string) => [...stockKeys.inventaires.details(), id] as const,
  },
} as const;

// Achats module query keys
const achatsKeys = {
  all: ["achats"] as const,
  fournisseurs: {
    all: () => [...achatsKeys.all, "fournisseurs"] as const,
    lists: () => [...achatsKeys.fournisseurs.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...achatsKeys.fournisseurs.lists(), filters || {}] as const,
    details: () => [...achatsKeys.fournisseurs.all(), "detail"] as const,
    detail: (id: number | string) => [...achatsKeys.fournisseurs.details(), id] as const,
    export: () => [...achatsKeys.fournisseurs.all(), "export"] as const,
  },
  fournitures: {
    all: () => [...achatsKeys.all, "fournitures"] as const,
    lists: () => [...achatsKeys.fournitures.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...achatsKeys.fournitures.lists(), filters || {}] as const,
    details: () => [...achatsKeys.fournitures.all(), "detail"] as const,
    detail: (id: number | string) => [...achatsKeys.fournitures.details(), id] as const,
  },
  commandes: {
    all: () => [...achatsKeys.all, "commandes"] as const,
    lists: () => [...achatsKeys.commandes.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...achatsKeys.commandes.lists(), filters || {}] as const,
    details: () => [...achatsKeys.commandes.all(), "detail"] as const,
    detail: (id: number | string) => [...achatsKeys.commandes.details(), id] as const,
  },
  receptions: {
    all: () => [...achatsKeys.all, "receptions"] as const,
    lists: () => [...achatsKeys.receptions.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...achatsKeys.receptions.lists(), filters || {}] as const,
  },
  factures: {
    all: () => [...achatsKeys.all, "factures"] as const,
    lists: () => [...achatsKeys.factures.all(), "list"] as const,
    list: (filters?: Record<string, unknown>) => [...achatsKeys.factures.lists(), filters || {}] as const,
  },
} as const;

// Notifications query keys
const notificationsKeys = {
  // Real-time notifications (used by Mercure - gets invalidated on new/delete)
  all: ["notifications"] as const,
  lists: () => [...notificationsKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) => [...notificationsKeys.lists(), filters] as const,
  details: () => [...notificationsKeys.all, "detail"] as const,
  detail: (id: number | string) => [...notificationsKeys.details(), id] as const,
  unreadCount: () => [...notificationsKeys.all, "unread-count"] as const,
  byModule: (module: string) => [...notificationsKeys.all, "byModule", module] as const,

  // Browser/All notifications - separate keys that Mercure doesn't touch
  // Used for the "All" tab with full-text search, filters, pagination
  browser: {
    all: ["notifications-browser"] as const,
    list: (filters?: Record<string, unknown>) => ["notifications-browser", "list", filters] as const,
  },
} as const;

/**
 * Dashboard query keys for conges and other dashboard-related queries
 */
const dashboardKeys = {
  all: ["dashboard"] as const,
  conges: {
    all: () => [...dashboardKeys.all, "conges"] as const,
    summary: () => [...dashboardKeys.conges.all(), "summary"] as const,
  },
} as const;

/**
 * Main query keys registry - exported for backward compatibility
 */
export const queryKeys = {
  auth: authKeys,
  users: usersKeys,
  permissions: permissionsKeys,
  collections: collectionsKeys,
  rh: rhKeys,
  pointage: pointageKeys,
  planning: planningKeys,
  creation: creationKeys,
  stock: stockKeys,
  achats: achatsKeys,
  notifications: notificationsKeys,
  apiResources,
} as const;

/**
 * Dashboard query keys - exported separately for modular access
 */
export const dashboardQueryKeys = dashboardKeys;

export type QueryKeys = typeof queryKeys;
export type DashboardQueryKeys = typeof dashboardQueryKeys;
